// Estanterías del depósito (PR 3, etapa 3.2). El mapa de códigos postales,
// la etiqueta que se pega en el estante y qué hay adentro de cada una.
//
// Dos cosas que la pantalla tiene que dejar claras porque no son obvias:
// cambiar el mapa NO mueve lo que ya está estanteado (avisa cuántos quedaron
// descolocados), y «Sin clasificar» no se toca: es el destino de todo lo que
// no mapea, para que siempre haya un lugar físico donde dejar el bulto.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

type Shelf = {
  id: number; warehouse_id: number; code: string; name: string; sort_order: number;
  active: boolean; unclassified: boolean; qr_token: string;
  postal_codes_count: number; deliveries_count: number;
};
type ShelfDelivery = {
  id: number; tracking: string | number | null; store: string | null; receiver: string | null;
  direccion: string | null; codigo_postal: string | null; postal_code_norm: string | null;
  tipo_envio: string | null; ops_status: string; geo_status: string; shelved_at: string | null;
};
type MapResult = {
  codes: string[]; from_zone?: string[]; invalid: string[]; unknown: string[];
  moved: { postal_code: string; from: string }[]; affected: number;
};

const OPS_LABEL: Record<string, string> = {
  ENTERED: "Ingresado", COLLECTED: "Colectado", ARRIVED_WAREHOUSE: "En planta", SHELVED: "Estanteado",
  ASSIGNED_TO_ROUTE: "En recorrido", OUT_FOR_DELIVERY: "En calle", DELIVERED: "Entregado",
  NOT_DELIVERED: "No entregado", CANCELLED: "Cancelado",
};

// «1 estantería», no «1 estanterías».
const plural = (n: number, singular: string, plural_: string) => `${n} ${n === 1 ? singular : plural_}`;

/* ===== Estilos (mismo lenguaje que RoutesPage.tsx) ===== */
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", verticalAlign: "top" };
const panelStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "18px", background: "rgba(255,255,255,0.03)", padding: "14px" };
const textareaStyle: React.CSSProperties = { width: "100%", minHeight: "150px", padding: "12px 14px", borderRadius: "16px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)", fontFamily: "monospace", fontSize: "14px" };
const badge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${color}`, color });

function Modal({ title, onClose, children, width = "820px" }: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; width?: string }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", zIndex: 100, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: width }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <h3 style={{ margin: 0, fontSize: "18px", color: "var(--text-primary)" }}>{title}</h3>
            <Button variant="ghost" onClick={onClose}>Cerrar</Button>
          </div>
          {children}
        </Card>
      </div>
    </div>
  );
}

// warehouseId/warehouseName opcionales (ver informe "LYNX — Multi-Warehouse,
// Dropoffs, Pickers y Estanterías", PR 2): sin ellos, mantiene el
// comportamiento legacy de siempre (el depósito único vía
// defaultWarehouseId() en el backend, texto "Depósito {id}" — la ruta
// /admin/deposito/estanterias existente no cambia). Pasados desde el
// detalle de un Warehouse en Sistema → Warehouses, administra ESE depósito
// puntual (sin defaultWarehouseId()) y muestra su nombre real.
export default function ShelvesPage({ warehouseId: fixedWarehouseId, warehouseName }: { warehouseId?: number; warehouseName?: string } = {}) {
  const { token } = useAuth();
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [warehouseId, setWarehouseId] = useState<number | null>(fixedWarehouseId ?? null);
  const warehouseQuery = fixedWarehouseId ? `?warehouse_id=${fixedWarehouseId}` : "";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [mapping, setMapping] = useState<Shelf | null>(null);
  const [contents, setContents] = useState<Shelf | null>(null);

  const req = useCallback(async (path: string, init: { method?: string; body?: unknown } = {}) => {
    const response = await authFetch(path, { token, method: init.method ?? "GET", json: init.body !== undefined, body: init.body === undefined ? undefined : JSON.stringify(init.body) });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || json?.ok === false) {
      const err: any = new Error(json?.error || json?.message || `Error ${response.status}`);
      // El servidor manda con qué CP se tropezó: la pantalla lo necesita para
      // ofrecer agregarlos al catálogo sin mandar al operador a otro lado.
      if (json?.unknown) err.unknown = json.unknown;
      throw err;
    }
    return json;
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const json = await req(`/v2/warehouse/shelves${warehouseQuery}`);
      setShelves(json.shelves ?? []);
      setWarehouseId(json.warehouse_id ?? null);
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }, [req, warehouseQuery]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg: string) => { setNotice(msg); window.setTimeout(() => setNotice(null), 6000); };
  const mapped = useMemo(() => shelves.reduce((n, s) => n + s.postal_codes_count, 0), [shelves]);
  const stored = useMemo(() => shelves.reduce((n, s) => n + s.deliveries_count, 0), [shelves]);

  // La etiqueta se abre en una pestaña aparte, lista para imprimir y pegar.
  // El SVG se sirve como archivo propio (blob) en vez de inyectarlo en el
  // documento: el navegador lo dibuja solo y nada de lo que vino del servidor
  // se ejecuta dentro del panel.
  const printLabel = async (shelf: Shelf) => {
    try {
      const json = await req(`/v2/warehouse/shelves/${shelf.id}/label`);
      const url = URL.createObjectURL(new Blob([json.label.svg], { type: "image/svg+xml" }));
      const win = window.open(url, "_blank");
      if (!win) { URL.revokeObjectURL(url); setError("El navegador bloqueó la pestaña de la etiqueta"); return; }
      win.addEventListener("load", () => { try { win.print(); } catch { /* el operador imprime a mano */ } });
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) { setError(e.message); }
  };

  const toggleActive = async (shelf: Shelf) => {
    try {
      await req(`/v2/warehouse/shelves/${shelf.id}`, { method: "PUT", body: { active: !shelf.active } });
      flash(`${shelf.code} ${shelf.active ? "desactivada" : "activada"}`);
      load();
    } catch (e: any) { setError(e.message); }
  };

  const removeShelf = async (shelf: Shelf) => {
    if (!window.confirm(`¿Borrar la estantería ${shelf.code}? Su mapa de códigos postales se pierde.`)) return;
    try {
      await req(`/v2/warehouse/shelves/${shelf.id}`, { method: "DELETE" });
      flash(`${shelf.code} borrada`);
      load();
    } catch (e: any) { setError(e.message); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Estanterías del depósito</h2>
            <p style={subtitleStyle}>
              Cada código postal va a una sola estantería. Lo que no mapea cae en «Sin clasificar», que siempre existe:
              así todo bulto tiene un lugar físico apenas entra. El tope de carga no vive acá, sino en el recorrido.
            </p>
          </div>
          <Button onClick={() => setCreating(true)}>Nueva estantería</Button>
        </div>
        <div style={{ ...smallMutedStyle, marginTop: "12px" }}>
          {warehouseName ?? `Depósito ${warehouseId ?? "—"}`} · {plural(shelves.length, "estantería", "estanterías")} · {plural(mapped, "código postal mapeado", "códigos postales mapeados")} · {plural(stored, "paquete ubicado", "paquetes ubicados")}
        </div>
      </Card>

      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)" }}>{error}</div> : null}
      {notice ? <div style={{ ...panelStyle, borderColor: "var(--success)", color: "var(--success)" }}>{notice}</div> : null}

      <Card>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Código</th>
                <th style={thStyle}>Nombre</th>
                <th style={thStyle}>Códigos postales</th>
                <th style={thStyle}>Paquetes</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {loading && !shelves.length ? (
                <tr><td style={tdStyle} colSpan={6}>Cargando…</td></tr>
              ) : !shelves.length ? (
                <tr><td style={tdStyle} colSpan={6}>Todavía no hay estanterías cargadas.</td></tr>
              ) : shelves.map((s) => (
                <tr key={s.id}>
                  <td style={{ ...tdStyle, fontWeight: 700, fontFamily: "monospace", fontSize: "15px" }}>{s.code}</td>
                  <td style={tdStyle}>{s.name}</td>
                  <td style={tdStyle}>
                    {s.unclassified
                      ? <span style={smallMutedStyle}>lo que no mapea</span>
                      : <>{s.postal_codes_count}{" "}
                        <button onClick={() => setMapping(s)} style={{ background: "none", border: "none", color: "var(--primary, #4f8cff)", cursor: "pointer", padding: 0 }}>editar</button></>}
                  </td>
                  <td style={tdStyle}>
                    {s.deliveries_count > 0
                      ? <button onClick={() => setContents(s)} style={{ background: "none", border: "none", color: "var(--primary, #4f8cff)", cursor: "pointer", padding: 0 }}>{s.deliveries_count}</button>
                      : <span style={smallMutedStyle}>vacía</span>}
                  </td>
                  <td style={tdStyle}>
                    {s.unclassified ? <span style={badge("var(--text-secondary)")}>Sin clasificar</span>
                      : s.active ? <span style={badge("var(--success)")}>Activa</span>
                      : <span style={badge("var(--warning, #d29922)")}>Inactiva</span>}
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <Button variant="secondary" onClick={() => printLabel(s)}>Etiqueta</Button>
                      {!s.unclassified ? <Button variant="ghost" onClick={() => toggleActive(s)}>{s.active ? "Desactivar" : "Activar"}</Button> : null}
                      {!s.unclassified ? <Button variant="ghost" onClick={() => removeShelf(s)}>Borrar</Button> : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {creating ? <CreateShelfModal req={req} warehouseId={fixedWarehouseId} onClose={() => setCreating(false)} onCreated={(code) => { setCreating(false); flash(`Estantería ${code} creada`); load(); }} onError={setError} /> : null}
      {mapping ? <PostalCodesModal shelf={mapping} req={req} onClose={() => { setMapping(null); load(); }} onSaved={(msg) => { setMapping(null); flash(msg); load(); }} onError={setError} /> : null}
      {contents ? <ContentsModal shelf={contents} req={req} onClose={() => setContents(null)} onError={setError} /> : null}
    </div>
  );
}

function CreateShelfModal({ req, warehouseId, onClose, onCreated, onError }: { req: any; warehouseId?: number; onClose: () => void; onCreated: (code: string) => void; onError: (m: string) => void }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [order, setOrder] = useState("0");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const json = await req("/v2/warehouse/shelves", { method: "POST", body: { code, name, sort_order: Number(order) || 0, warehouse_id: warehouseId } });
      onCreated(json.shelf.code);
    } catch (e: any) { onError(e.message); } finally { setSaving(false); }
  };

  return (
    <Modal title="Nueva estantería" onClose={onClose} width="560px">
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <Input label="Código" value={code} onChange={(e: any) => setCode(e.target.value)} placeholder="NORTE-2" />
        <p style={{ ...smallMutedStyle, margin: 0 }}>Es lo que se imprime en la etiqueta y no se puede cambiar después: letras, números y guiones, hasta 12 caracteres.</p>
        <Input label="Nombre" value={name} onChange={(e: any) => setName(e.target.value)} placeholder="Norte 2" />
        <Input label="Orden" type="number" value={order} onChange={(e: any) => setOrder(e.target.value)} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving || !code.trim()}>{saving ? "Guardando…" : "Crear"}</Button>
        </div>
      </div>
    </Modal>
  );
}

type ShelfZone = { zone_id: number; name: string; codes_count: number };
type ZoneOption = { id: number; name: string; codes_count: number };
type ZoneSync = { added: string[]; removed: string[]; adopted: string[]; moved: { postal_code: string; from: string }[]; affected: number };

function PostalCodesModal({ shelf, req, onClose, onSaved, onError }: { shelf: Shelf; req: any; onClose: () => void; onSaved: (msg: string) => void; onError: (m: string) => void }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<MapResult | null>(null);
  const [pendingUnknown, setPendingUnknown] = useState<string[] | null>(null);
  // Zonas vinculadas: sus CP los maneja la zona, no esta lista.
  const [linked, setLinked] = useState<ShelfZone[]>([]);
  const [fromZones, setFromZones] = useState(0);
  const [options, setOptions] = useState<ZoneOption[]>([]);
  const [toLink, setToLink] = useState("");
  const [zoneResult, setZoneResult] = useState<ZoneSync | null>(null);
  const [zoneError, setZoneError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [json, zs] = await Promise.all([
          req(`/v2/warehouse/shelves/${shelf.id}/postal-codes`),
          req("/v2/warehouse/zones").catch(() => ({ zones: [] })),
        ]);
        setText((json.manual ?? json.postal_codes ?? []).join("\n"));
        setLinked(json.zones ?? []);
        setFromZones((json.from_zones ?? []).length);
        setOptions(zs.zones ?? []);
      } catch (e: any) { onError(e.message); } finally { setLoading(false); }
    })();
  }, [req, shelf.id, onError]);

  const save = async (addMissing: boolean) => {
    setSaving(true); setPendingUnknown(null);
    try {
      const json = await req(`/v2/warehouse/shelves/${shelf.id}/postal-codes`, { method: "PUT", body: { codes: text, add_missing: addMissing } });
      const r: MapResult = json;
      setResult(r);
      setText(r.codes.join("\n"));
      if (!r.invalid.length && !r.moved.length && !r.affected && !r.from_zone?.length) {
        onSaved(`${shelf.code}: ${r.codes.length} códigos postales a mano${fromZones ? ` + ${fromZones} de zonas` : ""}`);
      }
    } catch (e: any) {
      // El servidor devuelve qué códigos no están en el catálogo: se ofrece
      // agregarlos, en vez de obligar a ir a otra pantalla.
      const unknown = e?.unknown;
      if (Array.isArray(unknown) && unknown.length) setPendingUnknown(unknown);
      onError(e.message);
    } finally { setSaving(false); }
  };

  // Vincular/desvincular se guarda en el acto: es lo que cambia el mapa.
  const setZones = async (zoneIds: number[]) => {
    setSaving(true); setZoneError(null); setZoneResult(null);
    try {
      const json = await req(`/v2/warehouse/shelves/${shelf.id}/zones`, { method: "PUT", body: { zone_ids: zoneIds } });
      setLinked(json.zones ?? []);
      setZoneResult(json);
      setToLink("");
      const fresh = await req(`/v2/warehouse/shelves/${shelf.id}/postal-codes`);
      setText((fresh.manual ?? []).join("\n"));
      setFromZones((fresh.from_zones ?? []).length);
    } catch (e: any) { setZoneError(e.message); } finally { setSaving(false); }
  };
  const available = options.filter((o) => !linked.some((l) => l.zone_id === o.id));

  return (
    <Modal title={`Códigos postales de ${shelf.code}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <div style={panelStyle}>
          <b style={{ color: "var(--text-primary)" }}>Zonas vinculadas</b>
          <p style={{ ...smallMutedStyle, margin: "4px 0 10px" }}>
            Los CP de una zona vinculada entran a esta estantería y los maneja la zona: si alguien cambia la zona (Sistema → Zonas), cambia
            este mapa. Si un CP ya estaba cargado a mano, pasa a la zona; si estaba en otra estantería, se mueve.
          </p>
          {linked.length ? (
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
              {linked.map((z) => (
                <span key={z.zone_id} style={{ ...badge("var(--primary, #4f8cff)"), display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  {z.name} · {z.codes_count} CP
                  <button onClick={() => setZones(linked.filter((l) => l.zone_id !== z.zone_id).map((l) => l.zone_id))} disabled={saving} title="Desvincular: saca sus CP de esta estantería" style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", padding: 0 }}>✕</button>
                </span>
              ))}
            </div>
          ) : <div style={{ ...smallMutedStyle, marginBottom: "10px" }}>Ninguna.</div>}
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
            <select id="shelf-zone-add" value={toLink} onChange={(e) => setToLink(e.target.value)} disabled={loading || saving}
              style={{ minHeight: "40px", padding: "0 10px", borderRadius: "12px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)", font: "inherit", minWidth: "240px" }}>
              <option value="">{options.length ? "Elegir zona…" : "No hay zonas (se crean en Sistema → Zonas)"}</option>
              {available.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.codes_count} CP)</option>)}
            </select>
            <Button variant="secondary" onClick={() => setZones([...linked.map((l) => l.zone_id), Number(toLink)])} disabled={!toLink || saving}>Vincular</Button>
          </div>
          {zoneError ? <div style={{ color: "var(--danger)", marginTop: "8px" }}>{zoneError}</div> : null}
          {zoneResult ? (
            <div style={{ marginTop: "8px", fontSize: "14px" }}>
              {[
                zoneResult.added.length ? `Se agregaron ${zoneResult.added.join(", ")}` : null,
                zoneResult.removed.length ? `Se quitaron ${zoneResult.removed.join(", ")}` : null,
                zoneResult.adopted.length ? `Pasaron a la zona (estaban a mano): ${zoneResult.adopted.join(", ")}` : null,
                zoneResult.moved.length ? `Se movieron de otra estantería: ${zoneResult.moved.map((m) => `${m.postal_code} (venía de ${m.from})`).join(", ")}` : null,
              ].filter(Boolean).join(" · ") || "Sin cambios en el mapa."}
              {zoneResult.affected ? <div style={{ color: "var(--warning, #d29922)" }}><b>{zoneResult.affected}</b> paquete(s) ya estanteados quedaron en la estantería vieja: hay que moverlos a mano.</div> : null}
            </div>
          ) : null}
        </div>

        <b style={{ color: "var(--text-primary)" }}>Cargados a mano{fromZones ? <span style={{ ...smallMutedStyle, fontWeight: 400 }}> · además hay {fromZones} CP que vienen de las zonas</span> : null}</b>
        <p style={{ ...smallMutedStyle, margin: 0 }}>
          Pegá la columna de una planilla: acepta comas, espacios y saltos de línea, y también CPA (B1704ABC).
          Se reemplaza esta lista entera (los CP de las zonas no se tocan desde acá). <b>Cambiar el mapa no mueve lo que ya está estanteado</b>: si algún bulto queda
          descolocado, se avisa acá.
        </p>
        {loading ? <p style={smallMutedStyle}>Cargando…</p> : (
          <textarea style={textareaStyle} value={text} onChange={(e) => setText(e.target.value)} placeholder={"1704\n1706\n1708"} />
        )}

        {result ? (
          <div style={{ ...panelStyle, borderColor: "var(--success)" }}>
            <div><b>{result.codes.length}</b> códigos postales cargados a mano en {shelf.code}.</div>
            {result.from_zone?.length ? <div>Ya los trae una zona vinculada (no se cargan a mano): {result.from_zone.join(", ")}</div> : null}
            {result.invalid.length ? <div style={{ color: "var(--warning, #d29922)" }}>No se pudieron leer: {result.invalid.join(", ")}</div> : null}
            {result.unknown.length ? <div>Agregados al catálogo geográfico: {result.unknown.join(", ")}</div> : null}
            {result.moved.length ? <div>Se movieron de otra estantería: {result.moved.map((m) => `${m.postal_code} (venía de ${m.from})`).join(", ")}</div> : null}
            {result.affected ? <div style={{ color: "var(--warning, #d29922)" }}><b>{result.affected}</b> paquete(s) ya estanteados quedaron en la estantería vieja: hay que moverlos a mano.</div> : null}
          </div>
        ) : null}

        {pendingUnknown ? (
          <div style={{ ...panelStyle, borderColor: "var(--warning, #d29922)" }}>
            <div>Estos códigos no están en el catálogo geográfico: <b>{pendingUnknown.join(", ")}</b>.</div>
            <div style={{ marginTop: "10px" }}>
              <Button variant="secondary" onClick={() => save(true)} disabled={saving}>Agregarlos al catálogo y guardar</Button>
            </div>
          </div>
        ) : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <Button variant="ghost" onClick={onClose}>Cerrar</Button>
          <Button onClick={() => save(false)} disabled={saving || loading}>{saving ? "Guardando…" : "Guardar CP a mano"}</Button>
        </div>
      </div>
    </Modal>
  );
}

function ContentsModal({ shelf, req, onClose, onError }: { shelf: Shelf; req: any; onClose: () => void; onError: (m: string) => void }) {
  const [rows, setRows] = useState<ShelfDelivery[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const json = await req(`/v2/warehouse/shelves/${shelf.id}/deliveries`);
        setRows(json.deliveries ?? []);
      } catch (e: any) { onError(e.message); } finally { setLoading(false); }
    })();
  }, [req, shelf.id, onError]);

  return (
    <Modal title={`Qué hay en ${shelf.code}`} onClose={onClose} width="960px">
      {loading ? <p style={smallMutedStyle}>Cargando…</p> : (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Tracking</th>
                <th style={thStyle}>Tienda</th>
                <th style={thStyle}>Destino</th>
                <th style={thStyle}>CP</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}>Estanteado</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length ? <tr><td style={tdStyle} colSpan={6}>La estantería está vacía.</td></tr> : rows.map((d) => (
                <tr key={d.id}>
                  <td style={{ ...tdStyle, fontFamily: "monospace" }}>{d.tracking ?? "—"}</td>
                  <td style={tdStyle}>{d.store ?? "—"}</td>
                  <td style={tdStyle}>
                    {d.receiver ?? "—"}<br />
                    <span style={smallMutedStyle}>{d.direccion ?? "sin dirección"}</span>
                    {d.geo_status === "pending" ? <><br /><span style={badge("var(--warning, #d29922)")}>Falta ubicación</span></> : null}
                  </td>
                  <td style={tdStyle}>{d.postal_code_norm ?? d.codigo_postal ?? "—"}</td>
                  <td style={tdStyle}>{OPS_LABEL[d.ops_status] ?? d.ops_status}</td>
                  <td style={tdStyle}>{d.shelved_at ? new Date(d.shelved_at).toLocaleString("es-AR") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
